/**
 * CollectionCapture
 *
 * Slide-up drawer for capturing data collection records.
 * Step 1: Select template → Step 2: Take photo → Step 3: Review AI-filled fields → Submit
 */

import { useState, useRef } from "react";
import {
	Drawer,
	Button,
	Input,
	Select,
	Rate,
	Switch,
	InputNumber,
	Spin,
	message,
} from "antd";
import {
	CameraOutlined,
	CheckOutlined,
	ArrowLeftOutlined,
} from "@ant-design/icons";
import {
	createRecord,
	type CollectionTemplate,
	type CollectionTemplateWithFields,
	type CollectionTemplateField,
} from "@/services/data-collection-service";
import { getTemplateById } from "@/services/data-collection-service";
import { uploadPhotoToFirebase } from "@/services/storage-service";
import "./CollectionCapture.scss";

const { TextArea } = Input;

interface CollectionCaptureProps {
	open: boolean;
	locationId: number;
	templates: CollectionTemplate[];
	onClose: () => void;
	onCreated: () => void;
}

type Step = "select" | "capture" | "review";

export const CollectionCapture = ({
	open,
	locationId,
	templates,
	onClose,
	onCreated,
}: CollectionCaptureProps) => {
	const [step, setStep] = useState<Step>("select");
	const [selectedTemplate, setSelectedTemplate] = useState<CollectionTemplateWithFields | null>(null);
	const [photoFile, setPhotoFile] = useState<File | null>(null);
	const [photoPreview, setPhotoPreview] = useState<string | null>(null);
	const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
	const [areaName, setAreaName] = useState("");
	const [notes, setNotes] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [loadingTemplate, setLoadingTemplate] = useState(false);
	const fileRef = useRef<HTMLInputElement>(null);

	const reset = () => {
		setStep("select");
		setSelectedTemplate(null);
		setPhotoFile(null);
		setPhotoPreview(null);
		setFieldValues({});
		setAreaName("");
		setNotes("");
		setSubmitting(false);
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	const handleSelectTemplate = async (tpl: CollectionTemplate) => {
		setLoadingTemplate(true);
		try {
			const full = await getTemplateById(tpl.id);
			setSelectedTemplate(full);
			setStep(full.requiresPhoto ? "capture" : "review");
		} catch {
			message.error("Failed to load template");
		} finally {
			setLoadingTemplate(false);
		}
	};

	const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setPhotoFile(file);
		setPhotoPreview(URL.createObjectURL(file));
		setStep("review");
	};

	const setFieldValue = (key: string, value: unknown) => {
		setFieldValues((prev) => ({ ...prev, [key]: value }));
	};

	const renderField = (field: CollectionTemplateField) => {
		const value = fieldValues[field.fieldKey];

		switch (field.fieldType) {
			case "TEXT":
				return (
					<Input
						placeholder={field.config?.placeholder as string || field.fieldLabel}
						value={(value as string) ?? ""}
						onChange={(e) => setFieldValue(field.fieldKey, e.target.value)}
					/>
				);
			case "TEXTAREA":
				return (
					<TextArea
						placeholder={field.config?.placeholder as string || field.fieldLabel}
						value={(value as string) ?? ""}
						onChange={(e) => setFieldValue(field.fieldKey, e.target.value)}
						rows={3}
					/>
				);
			case "NUMBER":
				return (
					<InputNumber
						placeholder={field.fieldLabel}
						value={value as number}
						onChange={(v) => setFieldValue(field.fieldKey, v)}
						addonAfter={field.config?.unit as string}
						style={{ width: "100%" }}
					/>
				);
			case "CURRENCY":
				return (
					<InputNumber
						placeholder={field.fieldLabel}
						value={value as number}
						onChange={(v) => setFieldValue(field.fieldKey, v)}
						prefix="$"
						style={{ width: "100%" }}
					/>
				);
			case "SELECT":
				return (
					<Select
						placeholder={`Select ${field.fieldLabel}`}
						value={value as string}
						onChange={(v) => setFieldValue(field.fieldKey, v)}
						options={(field.options || []).map((o) => ({ label: o, value: o }))}
						style={{ width: "100%" }}
					/>
				);
			case "MULTISELECT":
				return (
					<Select
						mode="multiple"
						placeholder={`Select ${field.fieldLabel}`}
						value={(value as string[]) ?? []}
						onChange={(v) => setFieldValue(field.fieldKey, v)}
						options={(field.options || []).map((o) => ({ label: o, value: o }))}
						style={{ width: "100%" }}
					/>
				);
			case "RATING":
				return (
					<Rate
						count={(field.config?.max as number) ?? 5}
						value={(value as number) ?? 0}
						onChange={(v) => setFieldValue(field.fieldKey, v)}
					/>
				);
			case "BOOLEAN":
				return (
					<Switch
						checked={!!value}
						onChange={(v) => setFieldValue(field.fieldKey, v)}
						checkedChildren="Yes"
						unCheckedChildren="No"
					/>
				);
			default:
				return (
					<Input
						placeholder={field.fieldLabel}
						value={(value as string) ?? ""}
						onChange={(e) => setFieldValue(field.fieldKey, e.target.value)}
					/>
				);
		}
	};

	const handleSubmit = async () => {
		if (!selectedTemplate) return;
		setSubmitting(true);

		try {
			let photoUrl: string | undefined;

			if (photoFile) {
				const folder = `data-collection/${locationId}`;
				const filename = `${Date.now()}_${photoFile.name}`;
				photoUrl = await uploadPhotoToFirebase(photoFile, folder, filename);
			}

			await createRecord({
				templateId: selectedTemplate.id,
				locationId,
				primaryPhotoUrl: photoUrl,
				areaName: areaName || undefined,
				notes: notes || undefined,
				fieldValues: Object.keys(fieldValues).length > 0 ? fieldValues : undefined,
			});

			message.success("Data collected successfully!");
			reset();
			onCreated();
		} catch (err: any) {
			message.error(err.message || "Failed to save collection");
		} finally {
			setSubmitting(false);
		}
	};

	const title =
		step === "select"
			? "Select Template"
			: step === "capture"
				? `Capture Photo — ${selectedTemplate?.name}`
				: `Review — ${selectedTemplate?.name}`;

	return (
		<Drawer
			open={open}
			onClose={handleClose}
			placement="bottom"
			height="90vh"
			title={title}
			className="collection-capture-drawer"
			destroyOnClose
		>
			<div className="cc-capture-content">
				{/* Step 1: Template Selection */}
				{step === "select" && (
					<div className="cc-template-list">
						{loadingTemplate && (
							<div className="cc-loading"><Spin /></div>
						)}
						{templates.length === 0 ? (
							<div className="cc-empty">
								No templates configured. Ask your admin to create collection templates.
							</div>
						) : (
							templates.map((tpl) => (
								<button
									key={tpl.id}
									type="button"
									className="cc-template-card"
									onClick={() => handleSelectTemplate(tpl)}
									disabled={loadingTemplate}
								>
									<div className="cc-template-card-icon">
										{tpl.icon || "📋"}
									</div>
									<div className="cc-template-card-info">
										<span className="cc-template-card-name">{tpl.name}</span>
										{tpl.category && (
											<span className="cc-template-card-category">{tpl.category}</span>
										)}
										{tpl.description && (
											<span className="cc-template-card-desc">{tpl.description}</span>
										)}
									</div>
								</button>
							))
						)}
					</div>
				)}

				{/* Step 2: Photo Capture */}
				{step === "capture" && (
					<div className="cc-photo-step">
						<div className="cc-photo-prompt">
							<CameraOutlined className="cc-photo-icon" />
							<p>Take a photo of what you are collecting data on</p>
							<Button
								type="primary"
								size="large"
								icon={<CameraOutlined />}
								onClick={() => fileRef.current?.click()}
							>
								Take Photo
							</Button>
							<input
								ref={fileRef}
								type="file"
								accept="image/*"
								capture="environment"
								style={{ display: "none" }}
								onChange={handlePhotoSelect}
							/>
							<Button
								type="link"
								onClick={() => setStep("review")}
								style={{ marginTop: 12 }}
							>
								Skip photo, enter data manually
							</Button>
						</div>
					</div>
				)}

				{/* Step 3: Review & Fill Fields */}
				{step === "review" && selectedTemplate && (
					<div className="cc-review-step">
						<Button
							type="text"
							icon={<ArrowLeftOutlined />}
							onClick={() => setStep(selectedTemplate.requiresPhoto ? "capture" : "select")}
							className="cc-back-btn"
						>
							Back
						</Button>

						{photoPreview && (
							<div className="cc-photo-preview">
								<img src={photoPreview} alt="Captured" />
							</div>
						)}

						<div className="cc-field-list">
							<div className="cc-field-item">
								<label className="cc-field-label">Area / Zone</label>
								<Input
									placeholder="e.g., Lobby, 2nd Floor, East Wing"
									value={areaName}
									onChange={(e) => setAreaName(e.target.value)}
								/>
							</div>

							{selectedTemplate.fields
								.sort((a, b) => a.displayOrder - b.displayOrder)
								.map((field) => (
									<div key={field.id} className="cc-field-item">
										<label className="cc-field-label">
											{field.fieldLabel}
											{field.isRequired && <span className="cc-required">*</span>}
										</label>
										{renderField(field)}
									</div>
								))}

							<div className="cc-field-item">
								<label className="cc-field-label">Notes</label>
								<TextArea
									placeholder="Additional observations..."
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									rows={3}
								/>
							</div>
						</div>

						<Button
							type="primary"
							size="large"
							block
							icon={<CheckOutlined />}
							onClick={handleSubmit}
							loading={submitting}
							className="cc-submit-btn"
						>
							Submit Collection
						</Button>
					</div>
				)}
			</div>
		</Drawer>
	);
};
